import React, { useState } from 'react'
import PerfectScrollbar from 'react-perfect-scrollbar'
import PropTypes from 'prop-types'
import { Search as SearchIcon } from 'react-feather'
import {
  Button,
  Hidden,
  Box,
  Card,
  CircularProgress,
  InputAdornment,
  TablePagination,
  TextField,
  Tooltip,
} from '@material-ui/core'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from 'components/Table'
import { ConfirmationDialog, MenuButton, Permitted } from 'components'

import helpers from 'helpers'
import constants from 'constants/index'
import { history } from 'service'
import { routes } from 'Routes'
import { reverse } from 'named-urls'
import { PHISHING_TUTORIAL_STEP_3 } from 'views/Phishing/driverSteps'

const PhishingTable = ({
  phishings,
  isLoading,
  isSearching,
  searchInput,
  handleSearchValue,
  onDelete,
  count,
  pagination = {},
}) => {
  const [deleteId, setDeleteId] = useState(null)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const { perPage, page, handleChangePage, handleChangeRowsPerPage } =
    pagination

  const handleConfirmDelete = async () => {
    setDeleteLoading(true)
    try {
      await onDelete(deleteId)
    } finally {
      setDeleteLoading(false)
      setDeleteId(null)
    }
  }

  return (
    <Box display="flex" flexDirection="column" gridGap={16}>
      <TextField
        fullWidth
        placeholder="Buscar por nome da campanha"
        value={searchInput}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon size={20} />
            </InputAdornment>
          ),
          endAdornment: isSearching && (
            <InputAdornment position="end">
              <CircularProgress size={20} />
            </InputAdornment>
          ),
        }}
        variant="outlined"
        onChange={handleSearchValue}
      />
      <Card id={PHISHING_TUTORIAL_STEP_3}>
        <PerfectScrollbar
          options={{ wheelPropagation: false, useBothWheelAxes: true }}
        >
          <Table
            emptyMessage="Nenhuma campanha encontrada."
            isLoading={isLoading}
            size="small"
            noWrap
          >
            <TableHead>
              <TableRow>
                <TableCell width="25%">Nome da campanha</TableCell>
                <TableCell width="13%">Data Agendada</TableCell>
                <TableCell align="center" width="10%">
                  <Tooltip title="Total de e-mails entregues aos participantes">
                    <span>Entregues</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center" width="10%">
                  <Tooltip title="Total de e-mails abertos pelos participantes">
                    <span>Visualizados</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center" width="10%">
                  <Tooltip title="Total de participantes que clicaram no link falso">
                    <span>Cliques</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center" width="10%">
                  <Tooltip title="Total de participantes que interagiram com o conteúdo falso">
                    <span>Comprometidos</span>
                  </Tooltip>
                </TableCell>
                <TableCell width="12%">Status</TableCell>
                <TableCell align="right" width="5%">
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {phishings?.map((phishing) => (
                <TableRow key={phishing?.id}>
                  <TableCell width="25%" applyNoWrap disableTooltip={false}>
                    {phishing?.name}
                  </TableCell>

                  <TableCell width="13%">
                    {phishing?.scheduleType ===
                    constants.phishing.DISPATCH_SCHEDULED
                      ? helpers.formatters.ptBrFormatDateTime(
                          phishing?.scheduledAt,
                        )
                      : constants.phishing.DISPATCH_OPTIONS.find(
                          (option) => option.value === phishing?.scheduleType,
                        )?.label || '-'}
                  </TableCell>
                  <TableCell align="center" width="10%">
                    {phishing?.phishingCampaignSummary
                      ? phishing.phishingCampaignSummary.delivered ?? 0
                      : '-'}
                  </TableCell>
                  <TableCell align="center" width="10%">
                    {phishing?.phishingCampaignSummary
                      ? phishing.phishingCampaignSummary.opened ?? 0
                      : '-'}
                  </TableCell>
                  <TableCell align="center" width="10%">
                    {phishing?.phishingCampaignSummary
                      ? phishing.phishingCampaignSummary.clicked ?? 0
                      : '-'}
                  </TableCell>
                  <TableCell align="center" width="10%">
                    {phishing?.phishingCampaignSummary
                      ? phishing.phishingCampaignSummary.compromised ?? 0
                      : '-'}
                  </TableCell>
                  <TableCell width="12%">
                    {constants.phishing.STATUS_LABEL[phishing?.status] || '-'}
                  </TableCell>
                  <TableCell align="right" width="5%">
                    <MenuButton>
                      <Button
                        color="secondary"
                        fullWidth
                        size="small"
                        onClick={() =>
                          history.push(
                            reverse(routes.phishing.show, {
                              phishingId: phishing?.id,
                            }),
                          )
                        }
                      >
                        Visualizar
                      </Button>
                      <Permitted tag={constants.permissions.PHISHING.MANAGE}>
                        {[
                          constants.phishing.DRAFT_STATUS_ID,
                          constants.phishing.SCHEDULED_STATUS_ID,
                        ].includes(phishing?.status) && (
                          <Button
                            color="secondary"
                            fullWidth
                            size="small"
                            onClick={() =>
                              history.push(
                                reverse(routes.phishing.edit, {
                                  phishingId: phishing?.id,
                                }),
                              )
                            }
                          >
                            Editar
                          </Button>
                        )}
                        {phishing?.status ===
                          constants.phishing.FINISHED_STATUS_ID && (
                          <Button
                            color="secondary"
                            fullWidth
                            size="small"
                            onClick={() => setDeleteId(phishing?.id)}
                          >
                            Excluir
                          </Button>
                        )}
                      </Permitted>
                    </MenuButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </PerfectScrollbar>
        <Box px={2}>
          <TablePagination
            component="div"
            count={count}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            page={page - 1}
            rowsPerPage={perPage}
            rowsPerPageOptions={[5, 10, 25, 100]}
            labelRowsPerPage={<Hidden smDown>Por página</Hidden>}
            nextIconButtonProps={{ size: 'small' }}
            backIconButtonProps={{ size: 'small' }}
          />
        </Box>
      </Card>
      <ConfirmationDialog
        open={!!deleteId}
        setOpen={(open) => !open && setDeleteId(null)}
        text="Deseja excluir esta campanha? Esta ação não pode ser desfeita."
        textButton="Excluir"
        textButtonCancel="Cancelar"
        variant="error"
        loading={deleteLoading}
        actionAcceptButton={handleConfirmDelete}
      />
    </Box>
  )
}

PhishingTable.propTypes = {
  phishings: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  isSearching: PropTypes.bool,
  searchInput: PropTypes.string,
  handleSearchValue: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  count: PropTypes.number,
  pagination: PropTypes.object,
}

export default PhishingTable
